import type { ReactNode } from 'react'
import { Copy, ListChecks, LockKeyhole, Plus, Send, Trash2 } from 'lucide-react'
import { Button } from '@highlight/ui'
import type { BillingPlanV2Tier } from '@highlight/common'
import type { AllowanceCount } from '@renderer/chat/hooks/use-plan-allowances'
import { planTierLabel } from '@renderer/components/pricing/plan-select-cards'
import { LockedPage, LockNotice, PlanUpgradeButton } from '@renderer/components/pricing/locked-page'
import { followUpsLeftCopy, followUpsOutCopy } from '@renderer/components/pricing/limits-copy'

// Nothing is drafted until it's unblurred, so the blur sits over stand-in prose shaped like a draft.
const STAND_IN_DRAFT = [
  'Hi there, thanks for the time today. Following up on what we covered so nothing slips.',
  'Here is where things landed, what I am taking on next, and when you can expect it from me.',
  'Let me know if I missed anything and I will update it before end of day.',
]

/** What a locked action would be once drafted, by its toolSet. */
export function lockedActionNoun(toolSet: string | undefined): string {
  switch (toolSet) {
    case 'gmail':
      return 'email'
    case 'slack':
      return 'message'
    case 'google_calendar':
      return 'invite'
    default:
      return 'action'
  }
}

/** The action's own button, drawn under the blur so the foot hazes the way the real one would. */
function standInSendButton(toolSet: string | undefined): { label: string; icon: ReactNode } {
  switch (toolSet) {
    case 'gmail':
      return { label: 'Send email', icon: <Send /> }
    case 'slack':
      return { label: 'Send message', icon: <Send /> }
    case 'google_calendar':
      return { label: 'Send invite', icon: <Send /> }
    default:
      return { label: 'Create', icon: <Plus /> }
  }
}

interface LockedActionPreviewProps {
  /** What the action is and its title, at the top of the page where the blur hasn't started. */
  header?: ReactNode
  toolSet: string | undefined
  /** The action's real fields (To, Subject…), drawn at the top of the page where the blur is lightest. */
  fields?: ReactNode
  /** The tier that unlocks every action. The Upgrade button wears its colour. */
  plan: BillingPlanV2Tier
  /** Unblurs this period; undefined while loading, null when they couldn't be read. */
  unlocks: AllowanceCount | null | undefined
  /** When the follow-ups reset, as "Nov 1". */
  resets: string
  isUnblurring: boolean
  onUnblur: () => void
  onUpgrade: () => void
  /** Archives the action. Absent where archiving it would close the surface around it. */
  onDismiss?: () => void
  className?: string
}

/**
 * The body of an action Free's plan holds back: its page stays in view, blurring
 * out toward the foot. With an unblur left this period it offers one; without,
 * the only way on is upgrading.
 */
export function LockedActionPreview({
  header,
  toolSet,
  fields,
  plan,
  unlocks,
  resets,
  isUnblurring,
  onUnblur,
  onUpgrade,
  onDismiss,
  className,
}: LockedActionPreviewProps) {
  const noun = lockedActionNoun(toolSet)
  const send = standInSendButton(toolSet)

  return (
    <LockedPage
      className={className}
      notice={
        unlocks !== undefined && (
          <LockedActionNotice
            noun={noun}
            plan={plan}
            unlocks={unlocks}
            resets={resets}
            isUnblurring={isUnblurring}
            onUnblur={onUnblur}
            onUpgrade={onUpgrade}
            onDismiss={onDismiss}
          />
        )
      }
    >
      <div className="flex flex-1 flex-col gap-2 p-2">
        {header}
        <div className="flex flex-1 flex-col px-3">
          {fields}
          <div className="flex flex-col gap-3 pt-1">
            {STAND_IN_DRAFT.map((line) => (
              <p key={line} className="type-prose-sm text-foreground m-0">
                {line}
              </p>
            ))}
          </div>
          <div className="flex-1" />
          <div className="flex items-center justify-end gap-2 pb-3 pt-6">
            <Button variant="outline" shape="square" aria-label="Delete">
              <Trash2 />
            </Button>
            <Button variant="outline" shape="square" aria-label="Copy">
              <Copy />
            </Button>
            <Button variant="primary">
              {send.icon}
              {send.label}
            </Button>
          </div>
        </div>
      </div>
    </LockedPage>
  )
}

function LockedActionNotice({
  noun,
  plan,
  unlocks,
  isUnblurring,
  onUnblur,
  onUpgrade,
  onDismiss,
  resets,
}: Pick<LockedActionPreviewProps, 'plan' | 'resets' | 'isUnblurring' | 'onUnblur' | 'onUpgrade' | 'onDismiss'> & {
  noun: string
  unlocks: AllowanceCount | null
}) {
  const tierLabel = planTierLabel(plan)
  const dismiss = onDismiss && (
    <Button variant="secondary" size="md" onClick={onDismiss}>
      Dismiss
    </Button>
  )

  // Spending the last unblur refetches the count to 0 before the action reopens; keep the Generating state meanwhile.
  if (unlocks && (unlocks.remaining > 0 || isUnblurring)) {
    const copy = followUpsLeftCopy(unlocks.remaining, unlocks.limit, resets)
    return (
      <LockNotice
        icon={<ListChecks className="size-5" />}
        title={copy.title}
        description={copy.body}
        actions={
          <>
            <Button variant="primary" size="md" disabled={isUnblurring} aria-busy={isUnblurring} onClick={onUnblur}>
              {isUnblurring ? 'Generating…' : 'Generate'}
            </Button>
            {dismiss}
          </>
        }
      />
    )
  }

  if (unlocks) {
    const copy = followUpsOutCopy(unlocks.limit, { plan: 'free', upgradeTo: plan, resets })
    return (
      <LockNotice
        icon={<LockKeyhole className="size-5" />}
        title={copy.title}
        description={copy.body}
        actions={
          <>
            <PlanUpgradeButton plan={plan} onClick={onUpgrade}>
              {copy.cta}
            </PlanUpgradeButton>
            {dismiss}
          </>
        }
      />
    )
  }

  // The count couldn't be read, so the notice sells the plan without quoting it.
  return (
    <LockNotice
      icon={<LockKeyhole className="size-5" />}
      title={`${tierLabel} drafts this ${noun} for you`}
      description={`Upgrade to ${tierLabel} for follow-ups on every meeting.`}
      actions={
        <>
          <PlanUpgradeButton plan={plan} onClick={onUpgrade}>{`Upgrade to ${tierLabel}`}</PlanUpgradeButton>
          {dismiss}
        </>
      }
    />
  )
}
